import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { projectsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Plus, 
  Video, 
  Play, 
  Clock, 
  Trash2,
  Settings,
  LogOut,
  Crown,
  MoreVertical,
  Folder,
  CheckCircle,
  Loader2,
  AlertCircle
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.list();
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = () => {
    navigate('/editor/new');
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    
    try {
      await projectsAPI.delete(projectId);
      setProjects(projects.filter(p => p.project_id !== projectId));
      toast.success('Project deleted');
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'processing':
      case 'analyzing':
      case 'cutting':
      case 'audio_sync':
      case 'captions':
      case 'color_grading':
      case 'effects':
      case 'encoding':
        return <Loader2 className="w-4 h-4 text-[#CCFF00] animate-spin" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-white/40" />;
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'created': 'Draft',
      'video_uploaded': 'Ready to process',
      'processing': 'Processing...',
      'analyzing': 'Analyzing...',
      'cutting': 'Cutting clips...',
      'audio_sync': 'Syncing audio...',
      'captions': 'Generating captions...',
      'color_grading': 'Color grading...',
      'effects': 'Adding effects...',
      'encoding': 'Encoding...',
      'completed': 'Completed',
      'error': 'Error'
    };
    return statusMap[status] || status;
  };

  return (
    <div className="min-h-screen bg-[#030303]">
      <div className="noise-overlay" />
      
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#0A0A0A] border-r border-white/5 p-6 flex flex-col">
        {/* Logo */}
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 rounded-xl bg-[#CCFF00] flex items-center justify-center">
            <Play className="w-5 h-5 text-black fill-black" />
          </div>
          <span className="font-heading font-bold text-xl text-white">DIDIT</span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-2">
          <Button
            variant="ghost"
            className="w-full justify-start text-white sidebar-active rounded-lg px-4 py-3"
            data-testid="nav-projects-btn"
          >
            <Folder className="w-5 h-5 mr-3" />
            Projects
          </Button>
          
          <Button
            variant="ghost"
            className="w-full justify-start text-white/60 hover:text-white hover:bg-white/5 rounded-lg px-4 py-3"
            onClick={() => navigate('/subscription')}
            data-testid="nav-subscription-btn"
          >
            <Crown className="w-5 h-5 mr-3" />
            Subscription
          </Button>

          {user?.role === 'admin' && (
            <Button
              variant="ghost"
              className="w-full justify-start text-white/60 hover:text-white hover:bg-white/5 rounded-lg px-4 py-3"
              onClick={() => navigate('/admin')}
              data-testid="nav-admin-btn"
            >
              <Settings className="w-5 h-5 mr-3" />
              Admin
            </Button>
          )}
        </nav>

        {/* User section */}
        <div className="border-t border-white/10 pt-6">
          <div className="flex items-center gap-3 mb-4">
            {user?.picture ? (
              <img 
                src={user.picture} 
                alt={user.name} 
                className="w-10 h-10 rounded-full"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-[#CCFF00]/20 flex items-center justify-center text-[#CCFF00] font-semibold">
                {user?.name?.charAt(0) || 'U'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{user?.name}</p>
              <p className="text-white/40 text-sm truncate">{user?.email}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 mb-4">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              user?.subscription_status === 'pro' 
                ? 'bg-[#CCFF00]/20 text-[#CCFF00]' 
                : 'bg-white/10 text-white/60'
            }`}>
              {user?.subscription_status === 'pro' ? 'Pro' : 'Free'}
            </span>
          </div>

          <Button
            variant="ghost"
            className="w-full justify-start text-white/60 hover:text-white hover:bg-white/5 rounded-lg"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading font-bold text-3xl text-white mb-2">Projects</h1>
              <p className="text-white/60">Create and manage your video projects</p>
            </div>
            <Button
              className="bg-[#CCFF00] text-black hover:bg-[#B3E600] rounded-full font-semibold px-6"
              onClick={handleCreateProject}
              data-testid="create-project-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Project
            </Button>
          </div>

          {/* Projects Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
            </div>
          ) : projects.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20"
            >
              <div className="w-20 h-20 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-6">
                <Video className="w-10 h-10 text-white/20" />
              </div>
              <h2 className="font-heading font-semibold text-xl text-white mb-2">No projects yet</h2>
              <p className="text-white/60 mb-6">Create your first project to get started</p>
              <Button
                className="bg-[#CCFF00] text-black hover:bg-[#B3E600] rounded-full font-semibold px-6"
                onClick={handleCreateProject}
                data-testid="empty-state-create-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create Project
              </Button>
            </motion.div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project, i) => (
                <motion.div
                  key={project.project_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Card 
                    className="bg-[#0A0A0A] border-white/10 rounded-2xl overflow-hidden card-hover cursor-pointer group"
                    onClick={() => navigate(`/editor/${project.project_id}`)}
                    data-testid={`project-card-${project.project_id}`}
                  >
                    {/* Thumbnail */}
                    <div className="aspect-video bg-[#121212] relative">
                      {project.video_url ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-[#CCFF00]/20 transition-colors">
                            <Play className="w-6 h-6 text-white group-hover:text-[#CCFF00]" />
                          </div>
                        </div>
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Video className="w-12 h-12 text-white/20" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-heading font-semibold text-white truncate flex-1">
                          {project.title}
                        </h3>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-white/40 hover:text-white">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-[#121212] border-white/10">
                            <DropdownMenuItem 
                              className="text-red-500 focus:text-red-500"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteProject(project.project_id);
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <div className="flex items-center gap-2 text-sm">
                        {getStatusIcon(project.status)}
                        <span className="text-white/60">{getStatusText(project.status)}</span>
                      </div>

                      <div className="flex items-center gap-4 mt-3 text-xs text-white/40">
                        <span className="capitalize">{project.video_type}</span>
                        <span>•</span>
                        <span>{new Date(project.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
